import { useQueryClient } from "@tanstack/react-query";
import { Check, Eye, UserCog } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useVediComeContesto, useVediComeStato } from "@/hooks/useVediCome";
import { clearVediCome, setVediCome, type VediComeStato } from "@/lib/vedi-come";
import { cn } from "@/lib/utils";

/**
 * Selettore «Vedi come» (solo MAITIME_STAFF): impersona un ruolo o un utente
 * per verificare la vista che vedranno, senza farli loggare. Sola lettura.
 */
export function VediComeSwitcher() {
  const { data: contesto } = useVediComeContesto();
  const attivo = useVediComeStato();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!contesto?.puo_impersonare) return null;

  function applica(stato: VediComeStato | null) {
    if (stato) {
      setVediCome(stato);
    } else {
      clearVediCome();
    }
    setOpen(false);
    // Rifai tutte le query con la nuova identità + riparti dalla dashboard.
    queryClient.invalidateQueries();
    navigate("/", { replace: true });
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors",
          attivo
            ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Vedi come (impersonazione per verifica)"
      >
        <Eye className="size-3.5" />
        <span className="hidden sm:inline">
          {attivo ? `Vedi come: ${attivo.label}` : "Vedi come"}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 max-h-[70vh] w-64 overflow-auto rounded-md border bg-popover shadow-md py-1 z-30"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => applica(null)}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-muted/40",
              !attivo && "font-medium"
            )}
          >
            <UserCog className="size-3.5 text-muted-foreground" />
            Io (Maitime)
            {!attivo && <Check className="ml-auto size-3.5" />}
          </button>

          {contesto.ruoli.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Ruoli
              </div>
              {contesto.ruoli.map((r) => {
                const sel = attivo?.tipo === "ruolo" && attivo.valore === r.valore;
                return (
                  <button
                    key={r.valore}
                    type="button"
                    role="menuitem"
                    onClick={() =>
                      applica({ tipo: "ruolo", valore: r.valore, label: r.label })
                    }
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-muted/40",
                      sel && "font-medium"
                    )}
                  >
                    {r.label}
                    {sel && <Check className="ml-auto size-3.5" />}
                  </button>
                );
              })}
            </>
          )}

          {contesto.utenti.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Utenti
              </div>
              {contesto.utenti.map((u) => {
                const sel = attivo?.tipo === "utente" && attivo.valore === u.email;
                return (
                  <button
                    key={u.email}
                    type="button"
                    role="menuitem"
                    onClick={() =>
                      applica({
                        tipo: "utente",
                        valore: u.email,
                        label: u.nome_completo,
                      })
                    }
                    className={cn(
                      "flex w-full flex-col items-start px-3 py-1.5 text-sm text-left transition-colors hover:bg-muted/40",
                      sel && "font-medium"
                    )}
                  >
                    <span className="flex w-full items-center gap-2">
                      {u.nome_completo}
                      {sel && <Check className="ml-auto size-3.5" />}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {u.email}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
